import React from 'react';
import { Callout, DocHeading, DocTable, Shot } from './components';

function PageCamera({ navigate }) {
  return (
    <div>
      <p className="text-sm text-brand-400 font-medium mb-2">Visuals</p>
      <h1 className="font-display text-3xl font-bold text-white tracking-tight mb-3">Camera</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        The camera tab lets you zoom out past the game&rsquo;s own limit, change the angle and the
        field of view, and detach the camera from your character. Everything is on{' '}
        <strong className="text-white">VISUALS &rsaquo; Camera</strong> and takes effect
        immediately &mdash; drag a slider and watch.
      </p>

      <Callout type="info">
        The Distance, Tilt, Rotation and Field of view sliders are percentages, not game units:{' '}
        <strong>0%</strong> is one end of what is possible and <strong>100%</strong> is the other.
        (Only the free camera sliders are in metres.) Your mouse keeps working as usual &mdash;
        you can still scroll and rotate, and the sliders follow what the game is doing.{' '}
        <strong>RESET EVERYTHING</strong> puts it all back.
      </Callout>

      <DocHeading>Your Settings Are Kept</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Once you change <strong className="text-white">Distance</strong>,{' '}
        <strong className="text-white">Tilt</strong>, <strong className="text-white">Rotation</strong>{' '}
        or <strong className="text-white">Field of view</strong> &mdash; with a slider or a button
        &mdash; the bot remembers that value. It is saved, so it comes back after a map change and
        the next time you start the game.
      </p>
      <DocTable
        headers={['When', 'What happens']}
        rows={[
          ['You change map', 'The game builds a fresh camera with its own values. For a few seconds after loading, the bot puts your values back until they stay, then lets go. Rotation is only set once, so it never fights your mouse.'],
          ['You scroll with the mouse wheel', 'Works as normal. Right after a map change the bot sets your distance once, and from then on the wheel is yours. Zooming with the wheel is not remembered — only what you set on this tab.'],
          ['You go to a different map', 'Your distance is kept in metres, so the view is the same everywhere. The percentage on the slider can be different, because every map has its own zoom range.'],
          ['The bot unloads', 'The camera is handed back to the game exactly as it was. Your saved values are applied again next time.'],
          [<strong className="text-white">RESET EVERYTHING</strong>, 'Everything goes back to how the game had it, and nothing is remembered after that.'],
        ]}
      />

      <DocHeading>Distance</DocHeading>
      <DocTable
        headers={['Control', 'What it does']}
        rows={[
          [<strong className="text-white">Distance</strong>, 'How far the camera sits from your character. 0% is as close as the game allows; the slider goes up to four times the game’s own zoom range.'],
          [<strong className="text-white">CLOSE</strong>, '0% — as close as the game allows.'],
          [<strong className="text-white">NORMAL</strong>, '25% — as far out as the game itself lets you scroll.'],
          [<strong className="text-white">FAR</strong>, '60% — well past the game’s limit.'],
          [<strong className="text-white">MAX</strong>, '100% — as far out as it goes.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        Past the game&rsquo;s own limit the camera slides straight back along the line it is
        looking, so the height does not jump &mdash; the card says the same:{' '}
        <em>Past the game&#39;s own limit the camera keeps sliding straight back - the height stays
        where it was.</em>
      </p>
      <Callout type="info">
        <strong>Zooming out helps the bot indirectly</strong>: ESP and the markers you place in the
        world are easier to work with when you can see the whole area.
      </Callout>
      <Callout type="warning">
        On a freshly loaded map the card can briefly say <em>Waiting for the game&#39;s own
        limits.</em> The slider needs the game&rsquo;s zoom range to know what 100% means, so it
        waits instead of guessing. It goes away on its own.
      </Callout>

      <DocHeading>Angle</DocHeading>
      <DocTable
        headers={['Control', 'What it does']}
        rows={[
          [<strong className="text-white">Tilt</strong>, '0% looks down, 100% looks level with the horizon.'],
          [<strong className="text-white">Rotation</strong>, 'Turns the camera around your character; 0–100% is one full circle.'],
          [<><strong className="text-white">TURN LEFT</strong> · <strong className="text-white">TURN RIGHT</strong></>, 'A quarter turn, without dragging the slider.'],
          [<strong className="text-white">TURN BACK</strong>, 'A half turn — look the other way.'],
          [<strong className="text-white">BACK TO WHERE IT WAS</strong>, 'Puts tilt and rotation back to what they were before the bot first changed the camera. It only appears once something has been changed.'],
        ]}
      />

      <Shot
        name="camera-01-camera-tab.png"
        tab="VISUALS › Camera"
        caption="The Camera tab after the camera has been adjusted."
        shoot="A VISUALS › Camera fül játékban, csatlakozott kamerával. Bal oszlop: DISTANCE, ANGLE, FIELD OF VIEW kártya; jobb oszlop: FREE CAMERA és STATUS. A STATUS-on látszik a zöld „connected”, a Delivery és a Zoom range sor, alatta a piros RESET EVERYTHING gomb. Előtte húzd meg egyszer a Tilt csúszkát, hogy a BACK TO WHERE IT WAS gomb is látsszon az ANGLE kártyán."
      />

      <DocHeading>Field of View</DocHeading>
      <DocTable
        headers={['Control', 'What it does']}
        rows={[
          [<strong className="text-white">Field of view</strong>, 'How wide the view is. 0% is half of the game’s default, 100% is double it, so the game’s default sits at about 33% (not in the middle). A wider view shows more, with more stretching at the edges.'],
          [<strong className="text-white">BACK TO THE GAME&#39;S OWN</strong>, 'Returns to the value the game had before the bot changed it.'],
          [<strong className="text-white">Match ESP projection</strong>, 'Keeps the bot’s markers in the world lined up with the game when the field of view changes. Off by default.'],
        ]}
      />
      <Callout type="warning">
        The bot&rsquo;s world markers &mdash; <button onClick={() => navigate('esp')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">ESP</button>{' '}
        boxes, pickup circles, route markers &mdash; are drawn with their own view angle. Whenever
        that angle differs from the game&rsquo;s and <strong>Match ESP projection</strong> is off,
        the card shows in orange: <em>World markers use a different angle - turn this on if they
        drift.</em> This can show even if you never touched the field of view, and on its own it
        is not a problem. Only if the markers slide away from the things they belong to, turn the
        switch on; if they sit worse with it on, turn it back off.
      </Callout>

      <DocHeading>Free Camera</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        <strong className="text-white">Detach from the character</strong> leaves the camera where
        it is instead of following you. Three sliders then move it, in metres, from the spot where
        you detached it: <strong>East / West</strong>, <strong>Height</strong> and{' '}
        <strong>North / South</strong> (each &minus;60 to +60 m).
      </p>
      <DocTable
        headers={['Control', 'What it does']}
        rows={[
          [<strong className="text-white">Detach from the character</strong>, 'On: the camera stops following you. Off: it follows you again.'],
          [<><strong className="text-white">East / West</strong> · <strong className="text-white">Height</strong> · <strong className="text-white">North / South</strong></>, 'Move the detached camera along the map’s directions (not relative to the screen).'],
          [<strong className="text-white">BACK TO THE CHARACTER</strong>, 'Sets all three sliders back to 0 — the camera returns to where it was when you detached it. It stays detached; switch Detach off to have it follow you again.'],
        ]}
      />
      <Callout type="warning">
        <strong>The camera stays where you leave it.</strong> Walk away with the camera detached and
        you will not see your character. If you get lost, switch{' '}
        <strong>Detach from the character</strong> off, or press <strong>RESET EVERYTHING</strong>.
        The free camera is not remembered: after a restart the camera follows you again.
      </Callout>

      <DocHeading>Status and Reset</DocHeading>
      <DocTable
        headers={['Line', 'What it means']}
        rows={[
          [<><strong className="text-white">Game camera</strong>: connected</>, 'Everything works.'],
          [<><strong className="text-white">Game camera</strong>: a reason</>, 'Why the camera cannot be reached right now, for example “no world loaded” or “no player entity (loading?)” — normal on the login screen and while loading. “camera property not in the list - patched game?” means the game was updated and the bot needs an update too.'],
          [<><strong className="text-white">Delivery</strong>: … sent</>, 'How many changes have reached the game.'],
          [<><strong className="text-white">Delivery</strong>: queue full - not sent</>, 'A change could not be passed to the game, typically on a loading screen. Slider changes are sent again automatically once you are in the world; RESET EVERYTHING may need a second press.'],
          [<strong className="text-white">Zoom range</strong>, 'The distance range of the slider in metres, and in brackets the game’s own limit.'],
          [<><strong className="text-white">Detach</strong>: waiting for the game thread</>, 'The switch is set, but the game has not taken it yet.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        <strong className="text-white">RESET EVERYTHING</strong> (the red button on the STATUS card)
        does what the text under it says:{' '}
        <em>Distance, angle, field of view, limits and the free camera - all back to how the game
        had them, and nothing is kept after that.</em> It also switches off Match ESP projection.
      </p>

      <DocHeading>Waiting for the Game Camera</DocHeading>
      <p className="text-sm text-gray-400">
        On the login screen, the character selection or during a map change the cards say{' '}
        <em>Waiting for the game camera.</em> That is normal &mdash; the tab comes alive as soon as
        your character is in the world.
      </p>
    </div>
  );
}

export default PageCamera;
